import React, { useState, useEffect } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, Platform, Modal } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { venueStyles } from '../styles/venueStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { CommunityPostCard } from '../components/CommunityPostCard';
import { BREAKPOINTS } from '../styles/breakpoints';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { useUser } from '../components/LoginPersistence';
import ProfileTabbedMediaManager from '../components/ProfileTabbedMediaManager';
import { Ionicons } from '@expo/vector-icons';

const OrganizationProfileScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const db = getFirestore();
  const { openMessaging } = useCommunityModal();
  const { user: currentUserData, setUser } = useUser();
  
  const [organizationData, setOrganizationData] = useState(null);
  const [members, setMembers] = useState([]);
  const [organizationPosts, setOrganizationPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [postToSave, setPostToSave] = useState(null);

  const { organizationId } = route.params;

  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  const loadOrganizationData = async () => {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (orgDoc.exists()) {
        const data = { id: orgDoc.id, ...orgDoc.data() };
        setOrganizationData(data);
        await Promise.all([
          loadMembers(data.members || [], data.ownerId),
          loadOrganizationPosts(orgDoc.id),
        ]);
      } else {
        console.error('Organization not found');
      }
    } catch (error) {
      console.error('Error loading organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationPosts = async (targetOrganizationId) => {
    if (!targetOrganizationId) {
      setOrganizationPosts([]);
      setLoadingPosts(false);
      return;
    }
    try {
      setLoadingPosts(true);
      const postsQuery = query(
        collection(db, 'communityPosts'),
        where('postAsOrganizationId', '==', targetOrganizationId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const posts = postsSnapshot.docs.map((postDoc) => ({
        id: postDoc.id,
        ...postDoc.data(),
      }));
      posts.sort((a, b) => {
        const aTs = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTs = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTs - aTs;
      });
      setOrganizationPosts(posts);
    } catch (error) {
      console.error('Error loading organization posts:', error);
      setOrganizationPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const loadMembers = async (memberIds, ownerId) => {
    if (!memberIds || memberIds.length === 0) {
      setLoadingMembers(false);
      return;
    }

    try {
      setLoadingMembers(true);
      const memberPromises = memberIds.map(async (memberId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              id: memberId,
              ...userData,
              role: memberId === ownerId ? 'owner' : 'member'
            };
          }
          return null;
        } catch (error) {
          console.error(`Error loading member ${memberId}:`, error);
          return null;
        }
      });

      const memberData = await Promise.all(memberPromises);
      const validMembers = memberData.filter(m => m !== null);
      // Sort: owner first, then members
      validMembers.sort((a, b) => {
        if (a.role === 'owner') return -1;
        if (b.role === 'owner') return 1;
        return 0;
      });
      setMembers(validMembers);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleMessageOrganization = () => {
    if (!currentUser?.uid) {
      alert('Please log in to send messages');
      return;
    }

    // Check if current user is a community member
    if (!currentUserData?.isCommunityMember) {
      alert('You must be a community member to send messages');
      return;
    }

    try {
      // Open the community modal and navigate to messages tab with target organization
      openMessaging(organizationId, null);
    } catch (error) {
      console.error('Error calling openMessaging:', error);
    }
  };

  if (loading) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={globalStyles.overlay}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#4e7de0" />
            <Text style={styles.loadingText}>Loading organization...</Text>
          </View>
        </View>
      </View>
    );
  }

  if (!organizationData) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={globalStyles.overlay}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Organization not found</Text>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.backButtonText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[globalStyles.container, { width: '100%' }]}>
      <BackgroundVideo />
      <View style={[globalStyles.overlay, { width: '100%' }]}>
        <ScrollView style={styles.scrollView}>
          {/* Content Container */}
          <View style={styles.contentContainer}>
            {/* Back Button */}
            <TouchableOpacity 
              style={venueStyles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={venueStyles.backButtonText}>← Back</Text>
            </TouchableOpacity>

            {/* Organization Card */}
            <View style={styles.profileContainer}>
              <View style={[styles.profileCard, {
                backgroundColor: '#FFE99C',
                borderWidth: 3,
                borderColor: organizationData.isPublic === false ? '#4caf50' : organizationData.isPublic === true ? '#ff3b30' : '#666769',
                borderStyle: 'solid'
              }]}>
                
                {/* Organization Photo */}
                <View style={styles.profilePhotoContainer}>
                  {organizationData.photoURL ? (
                    <Image source={{ uri: organizationData.photoURL }} style={styles.profilePhoto} />
                  ) : (
                    <View style={styles.profilePhotoPlaceholder}>
                      <Text style={styles.profilePhotoPlaceholderText}>
                        {(organizationData.username || organizationData.title || 'O')[0].toUpperCase()}
                      </Text>
                    </View>
                  )}
                </View>

                {/* Organization Name */}
                <Text style={[styles.profileName, { color: '#000000' }]}>
                  {organizationData.username || organizationData.title || 'Organization'}
                </Text>

                {/* Organization Type Badge */}
                <View style={styles.organizationBadge}>
                  <Text style={styles.organizationBadgeText}>Organization</Text>
                </View>

                {/* Location */}
                {organizationData.location && (
                  <View style={styles.locationContainer}>
                    <Text style={styles.locationLabel}>Location:</Text>
                    <Text style={styles.locationText}>{organizationData.location}</Text>
                  </View>
                )}

                {/* Bio */}
                {organizationData.bio && (
                  <View style={styles.bioContainer}>
                    <Text style={styles.bioLabel}>About:</Text>
                    <Text style={styles.bioText}>{organizationData.bio}</Text>
                  </View>
                )}

                {/* Message Button - Only show if current user is a community member */}
                {currentUser?.uid && currentUserData?.isCommunityMember && (
                  <TouchableOpacity
                    style={styles.messageButton}
                    onPress={handleMessageOrganization}
                  >
                    <Text style={styles.messageButtonText}>
                      Message Organization
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Organization Stats */}
                <View style={styles.statsContainer}>
                  <Text style={styles.statsText}>
                    {members.length} {members.length === 1 ? 'member' : 'members'}
                  </Text>
                  <Text style={styles.statsText}>
                    Created {organizationData.createdAt ? new Date(organizationData.createdAt.toDate()).toLocaleDateString() : 'Recently'}
                  </Text>
                </View>
              </View>

              {/* Members Section */}
              {members.length > 0 && (
                <View style={styles.membersSection}>
                  <Text style={styles.membersSectionTitle}>Members</Text>
                  {loadingMembers ? (
                    <View style={styles.membersLoadingContainer}>
                      <ActivityIndicator size="small" color="#4e7de0" />
                      <Text style={styles.membersLoadingText}>Loading members...</Text>
                    </View>
                  ) : (
                    <View style={styles.membersGrid}>
                      {members.map((member) => (
                        <TouchableOpacity
                          key={member.id}
                          style={styles.memberCard}
                          onPress={() => navigation.navigate('UserProfileScreen', { userId: member.id })}
                        >
                          <View style={styles.memberPhotoContainer}>
                            {member.photoURL ? (
                              <Image source={{ uri: member.photoURL }} style={styles.memberPhoto} />
                            ) : (
                              <View style={styles.memberPhotoPlaceholder}>
                                <Text style={styles.memberPhotoPlaceholderText}>
                                  {(member.firstName || member.username || '?')[0].toUpperCase()}
                                </Text>
                              </View>
                            )}
                            {member.role === 'owner' && (
                              <View style={styles.ownerBadge}>
                                <Text style={styles.ownerBadgeText}>Owner</Text>
                              </View>
                            )}
                          </View>
                          <Text style={styles.memberName} numberOfLines={1}>
                            {member.username || member.firstName || 'Anonymous'}
                          </Text>
                          <Text style={styles.memberRole}>
                            {member.role === 'owner' ? 'Owner' : 'Member'}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </View>
              )}

              
            </View>

            <Modal
              visible={showFolderSelectModal && !!postToSave}
              transparent
              animationType="fade"
              onRequestClose={() => { setShowFolderSelectModal(false); setPostToSave(null); }}
            >
              <View style={styles.modalOverlay}>
                <View style={styles.folderSelectModalContent}>
                  <View style={styles.folderSelectHeader}>
                    <Text style={styles.folderSelectHeaderText}>Select folder to save post</Text>
                    <TouchableOpacity onPress={() => { setShowFolderSelectModal(false); setPostToSave(null); }} style={{ padding: 8 }}>
                      <Ionicons name="close" size={24} color="#1d1d1d" />
                    </TouchableOpacity>
                  </View>
                  <View style={{ flex: 1 }}>
                    {currentUserData && (
                      <ProfileTabbedMediaManager
                        libraryTab="community"
                        userData={currentUserData}
                        setUser={setUser}
                        onEditReport={() => {}}
                        onDeleteReport={() => {}}
                        onViewVenue={() => {}}
                        onAddVenuePhoto={() => {}}
                        onDeleteVenuePhoto={() => {}}
                        onFolderSelect={() => { setShowFolderSelectModal(false); setPostToSave(null); }}
                        communityPostToSave={postToSave}
                        allowFileUpload={false}
                        allowProfileDropImport={false}
                      />
                    )}
                  </View>
                </View>
              </View>
            </Modal>
          </View>
          <View style={styles.postsContainer}>
                <View style={styles.postsSeparator} />
                {loadingPosts ? (
                  <View style={styles.membersLoadingContainer}>
                    <ActivityIndicator size="small" color="#4e7de0" />
                    <Text style={styles.membersLoadingText}>Loading posts...</Text>
                  </View>
                ) : organizationPosts.length > 0 ? (
                  <View style={styles.postsList}>
                    {organizationPosts.map((post) => (
                      <View key={post.id} style={styles.postCardWrap}>
                        <CommunityPostCard
                          post={post}
                          onPress={() => {}}
                          onMessagePress={() => handleMessageOrganization()}
                          onFolderPress={(selectedPost) => {
                            if (!currentUserData?.uid) {
                              alert('Please sign in to save posts to a folder.');
                              return;
                            }
                            setPostToSave(selectedPost);
                            setShowFolderSelectModal(true);
                          }}
                        />
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.membersLoadingText}>No posts yet.</Text>
                )}
              </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    width: '100%',
  },
  contentContainer: {
    width: '100%',
    paddingHorizontal: 20,
    marginHorizontal: 'auto',
    paddingTop: Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.tablet ? 80 : 60,
    alignItems: 'center',
    paddingBottom: 20,
  },
  profileContainer: {
    width: '100%',
    maxWidth: 500,
    minWidth: 300,
    alignItems: 'center',
    marginTop: 20,
  },
  profileCard: {
    width: '100%',
    padding: 30,
    borderRadius: 20,
    alignItems: 'center',
    position: 'relative',
    minHeight: 400,
  },
  profilePhotoContainer: {
    marginBottom: 20,
    position: 'relative',
  },
  profilePhoto: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 4,
    borderColor: '#000000',
  },
  profilePhotoPlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: '#000000',
  },
  profilePhotoPlaceholderText: {
    color: '#000000',
    fontSize: 48,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  profileName: {
    fontSize: 28,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  organizationBadge: {
    backgroundColor: '#FFEB3B',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    marginBottom: 15,
  },
  organizationBadgeText: {
    color: '#000000',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  locationContainer: {
    width: '100%',
    marginBottom: 15,
  },
  locationLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  locationText: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    color: '#000000',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: 12,
    borderRadius: 8,
  },
  bioContainer: {
    width: '100%',
    marginBottom: 20,
  },
  bioLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  bioText: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    color: '#000000',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: 12,
    borderRadius: 8,
    lineHeight: 22,
    minHeight: 60,
  },
  messageButton: {
    backgroundColor: '#4e7de0',
    borderRadius: 25,
    paddingVertical: 15,
    paddingHorizontal: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  messageButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  statsContainer: {
    marginTop: 20,
    alignItems: 'center',
  },
  statsText: {
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    color: 'rgba(0, 0, 0, 0.6)',
    marginBottom: 4,
  },
  membersSection: {
    width: '100%',
    marginTop: 30,
    padding: 20,
    backgroundColor: '#ffffff',
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#FFE99C',
  },
  membersSectionTitle: {
    fontSize: 24,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 20,
    textAlign: 'center',
  },
  membersLoadingContainer: {
    alignItems: 'center',
    padding: 20,
  },
  membersLoadingText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginTop: 10,
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    ...(Platform.OS === 'web' && {
      gap: '15px',
    }),
    ...(Platform.OS !== 'web' && {
      gap: 15,
    }),
  },
  memberCard: {
    ...(Platform.OS === 'web' && {
      width: 'calc(33.333% - 10px)',
      cursor: 'pointer',
      transition: 'transform 0.2s ease',
      ':hover': {
        transform: 'scale(1.05)',
      },
    }),
    ...(Platform.OS !== 'web' && {
      width: '48%',
    }),
    minWidth: 120,
    maxWidth: 200,
    backgroundColor: '#f9f9f9',
    borderRadius: 12,
    padding: 15,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    marginBottom: Platform.OS !== 'web' ? 15 : 0,
  },
  memberPhotoContainer: {
    position: 'relative',
    marginBottom: 10,
  },
  memberPhoto: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 2,
    borderColor: '#000000',
  },
  memberPhotoPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#000000',
  },
  memberPhotoPlaceholderText: {
    color: '#000000',
    fontSize: 32,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  ownerBadge: {
    position: 'absolute',
    bottom: -5,
    right: -5,
    backgroundColor: '#FFEB3B',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#000000',
  },
  ownerBadgeText: {
    color: '#000000',
    fontSize: 9,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  memberName: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
    textAlign: 'center',
    width: '100%',
  },
  memberRole: {
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    color: 'rgba(0, 0, 0, 0.6)',
    textAlign: 'center',
  },
  postsList: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 10,
  },
  postCardWrap: {
    maxWidth: 450,
    width: '100%',
  },
  postsContainer: {
    width: '100%',
    marginTop: 12,
    padding: 20,
  },
  postsSeparator: {
    height: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    marginBottom: 12,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorText: {
    color: '#ffffff',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 20,
    textAlign: 'center',
  },
  backButton: {
    backgroundColor: '#4e7de0',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 20,
  },
  backButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  folderSelectModalContent: {
    width: '100%',
    maxWidth: 1100,
    height: '90%',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  folderSelectHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  folderSelectHeaderText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    fontWeight: 'bold',
    color: '#222',
  },
});

export default OrganizationProfileScreen;

